import React, { useState } from "react";
import { smsSettingsRequest, type SmsSettings } from "@/services/smsSettingsService";
import "./SmsNotificationSettings.css";

export default function SmsNotificationSettings() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState<SmsSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");

  async function load() {
    setOpen(true);
    setBusy(true);
    setError("");
    try { setSettings(await smsSettingsRequest()); }
    catch { setError("SMS settings are unavailable in this environment."); }
    finally { setBusy(false); }
  }
  async function save() {
    if (!settings) return;
    setBusy(true);
    setError("");
    setSaved("");
    try {
      const result = await smsSettingsRequest({ phone: settings.phone, taskEnabled: settings.taskEnabled, quotationEnabled: settings.quotationEnabled });
      setSettings(result);
      setSaved("Notification preferences saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save SMS settings.");
    } finally { setBusy(false); }
  }
  const change = (patch: Partial<SmsSettings>) => {
    setSettings(current => current ? { ...current, ...patch } : current);
    setSaved("");
  };
  return (
    <section className="sms-notification-settings" aria-label="SMS notification settings">
      <button type="button" onClick={() => open ? setOpen(false) : void load()} aria-expanded={open}>
        {open ? "Close SMS settings" : "SMS settings"}
      </button>
      {open && (
        <div className="sms-notification-settings__content">
          <p>Quotation signing and overdue invoice SMS require individual review. Email and in-app notifications continue.</p>
          <a href="/notifications/sms">Review pending SMS notifications</a>
          {busy && <p role="status">Loading or saving settings...</p>}
          {error && <p role="alert">{error}</p>}
          {saved && <p role="status">{saved}</p>}
          {settings && (
            <>
              <p><strong>{settings.companyName}</strong> · Provider: {settings.provider}</p>
              <form onSubmit={event => { event.preventDefault(); void save(); }}>
                <fieldset disabled={busy}>
                  <legend>Your notification preferences</legend>
                  <label>Your mobile for receiving notifications
                    <input type="tel" autoComplete="tel" inputMode="tel" value={settings.phone} maxLength={64}
                      placeholder="04xx xxx xxx" onChange={event => change({ phone: event.target.value })} />
                  </label>
                  <p>This receiving number is unverified and does not change sign-in or account recovery. Messages come from the company phone. Use these preferences to opt out; replies go to the company phone.</p>
                  <label className="sms-notification-settings__check">
                    <input type="checkbox" checked={settings.taskEnabled} onChange={event => change({ taskEnabled: event.target.checked })} />
                    Allow task assignment SMS when this event becomes available
                  </label>
                  <label className="sms-notification-settings__check">
                    <input type="checkbox" checked={settings.quotationEnabled} onChange={event => change({ quotationEnabled: event.target.checked })} />
                    Allow quotation signed SMS
                  </label>
                  <p>Signing notifications go to YL for structural work and WD for civil work, using each engineer's notification mobile and opt-in. Optional service notifications only. Turn these off here to opt out.</p>
                  <button type="submit">Save preferences</button>
                </fieldset>
              </form>
            </>
          )}
        </div>
      )}
    </section>
  );
}
